import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom'

import LandingPage from './components/LandingPage'
import Login from './components/Login'
import Dashboard from './components/Dashboard'
import AgentDashboard from './components/AgentDashboard'
import SuperAdminDashboard from './components/SuperAdminDashboard'
import ImpersonationBar from './components/ImpersonationBar'
import RealTimeNotifications from './components/RealTimeNotifications'
import ErrorBoundary from './components/ErrorBoundary'
import PWAInstallPrompt from './components/PWAInstallPrompt'
import './index.css'

/**
 * ArtinSmartRealty V2 — App Entry Point
 * React Router v6 with role-based protected routes.
 *
 * Routes:
 *   /             → LandingPage (public)
 *   /login        → Login (public, redirects if already authed)
 *   /dashboard/*  → Dashboard (admin) | SuperAdmin impersonation view
 *   /super-admin  → SuperAdminDashboard (super_admin only)
 *   /agent        → AgentDashboard (agent only)
 */

// ─────────────────────────────────────────────────────────────────────────────
// Auth context helpers (localStorage-based, no external context needed)
// ─────────────────────────────────────────────────────────────────────────────

function getStoredUser () {
  const token = localStorage.getItem('token')
  const tenantId = localStorage.getItem('tenantId')
  if (!token || !tenantId) return null
  return {
    token,
    tenant_id:      parseInt(tenantId),
    name:           localStorage.getItem('userName') || '',
    email:          localStorage.getItem('userEmail') || '',
    is_super_admin: localStorage.getItem('isSuperAdmin') === 'true',
    role:           localStorage.getItem('userRole') || 'admin',
  }
}

function clearStoredUser () {
  ['token','tenantId','userName','userEmail','isSuperAdmin','userRole'].forEach(k =>
    localStorage.removeItem(k)
  )
}

function saveUser (userData, role) {
  localStorage.setItem('token',       userData.access_token)
  localStorage.setItem('tenantId',    String(userData.tenant_id))
  localStorage.setItem('userName',    userData.name || '')
  localStorage.setItem('userEmail',   userData.email || '')
  localStorage.setItem('isSuperAdmin', userData.is_super_admin ? 'true' : 'false')
  localStorage.setItem('userRole',    role)
}

// ─────────────────────────────────────────────────────────────────────────────
// ProtectedRoute — wraps any route that requires authentication
// ─────────────────────────────────────────────────────────────────────────────

function ProtectedRoute ({ children, requiredRole }) {
  const user = getStoredUser()

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (requiredRole && user.role !== requiredRole) {
    // Redirect to the correct home for the actual role
    if (user.is_super_admin) return <Navigate to="/super-admin" replace />
    if (user.role === 'agent') return <Navigate to="/agent" replace />
    return <Navigate to="/dashboard" replace />
  }

  return children
}

// ─────────────────────────────────────────────────────────────────────────────
// LoginRoute — if already authed, skip login page
// ─────────────────────────────────────────────────────────────────────────────

function LoginRoute ({ onLogin }) {
  const user = getStoredUser()
  const navigate = useNavigate()

  if (user) {
    if (user.is_super_admin) return <Navigate to="/super-admin" replace />
    if (user.role === 'agent') return <Navigate to="/agent" replace />
    return <Navigate to="/dashboard" replace />
  }

  const handleLogin = (userData) => {
    let role = 'admin'
    if (userData.is_super_admin) role = 'super_admin'
    else if (userData.role)       role = userData.role

    saveUser(userData, role)
    onLogin({ ...userData, role })

    if (userData.is_super_admin) navigate('/super-admin', { replace: true })
    else if (role === 'agent')   navigate('/agent',       { replace: true })
    else                         navigate('/dashboard',   { replace: true })
  }

  return <Login onLogin={handleLogin} />
}

// ─────────────────────────────────────────────────────────────────────────────
// SuperAdminRoute — handles impersonation state
// ─────────────────────────────────────────────────────────────────────────────

function SuperAdminRoute ({ user, onLogout }) {
  const [selectedTenant, setSelectedTenant] = useState(null)

  const handleImpersonate = (tenant) => {
    sessionStorage.setItem('admin_fallback_token', user.token)
    sessionStorage.setItem('impersonating_tenant', JSON.stringify(tenant))
    setSelectedTenant(tenant)
  }

  const handleExitImpersonation = () => {
    const adminToken = sessionStorage.getItem('admin_fallback_token')
    if (adminToken) localStorage.setItem('token', adminToken)
    sessionStorage.removeItem('admin_fallback_token')
    sessionStorage.removeItem('impersonating_tenant')
    setSelectedTenant(null)
  }

  if (selectedTenant) {
    const viewAsUser = {
      ...user,
      tenant_id: selectedTenant.id,
      name:      selectedTenant.name,
      token:     user.token,
      role:      'admin',
    }
    // Dashboard already includes RealTimeNotifications — no double wrap
    return (
      <div>
        <ImpersonationBar
          tenantName={selectedTenant.name || selectedTenant.email}
          onExit={handleExitImpersonation}
        />
        <div className="pt-16">
          <Dashboard user={viewAsUser} onLogout={onLogout} />
        </div>
      </div>
    )
  }

  return (
    <SuperAdminDashboard
      user={user}
      onLogout={onLogout}
      onImpersonate={handleImpersonate}
    />
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// App Root
// ─────────────────────────────────────────────────────────────────────────────

function App () {
  const [user, setUser] = useState(getStoredUser)

  const handleLogin  = (userData) => setUser(userData)
  const handleLogout = () => {
    clearStoredUser()
    setUser(null)
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* ── Public ── */}
        <Route path="/" element={<LandingPage />} />

        <Route
          path="/login"
          element={<LoginRoute onLogin={handleLogin} />}
        />

        {/* ── Super Admin ── */}
        <Route
          path="/super-admin"
          element={
            <ProtectedRoute requiredRole="super_admin">
              {user && (
                <SuperAdminRoute user={user} onLogout={handleLogout} />
              )}
            </ProtectedRoute>
          }
        />

        {/* ── Tenant Admin Dashboard ── */}
        <Route
          path="/dashboard/*"
          element={
            <ProtectedRoute requiredRole="admin">
              {user && (
                <RealTimeNotifications>
                  <Dashboard user={user} onLogout={handleLogout} />
                </RealTimeNotifications>
              )}
            </ProtectedRoute>
          }
        />

        {/* ── Sales Agent ── */}
        <Route
          path="/agent"
          element={
            <ProtectedRoute requiredRole="agent">
              {user && (
                <RealTimeNotifications>
                  <AgentDashboard user={user} onLogout={handleLogout} />
                </RealTimeNotifications>
              )}
            </ProtectedRoute>
          }
        />

        {/* ── Fallback ── */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
      <PWAInstallPrompt />
    </ErrorBoundary>
  </React.StrictMode>,
)
